import type { Store } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { createStore, updateStore } from "../../core/ops/store-ops";
import { hasErrors } from "../../core/primitives/form-errors";
import { busyForm, failedForm, idleForm } from "../../core/primitives/load-status";
import { hostsShopPage } from "../../core/primitives/shop-page";
import {
  draftToRequest,
  type ProfileField,
  storeToDraft,
  validateProfileDraft,
  withPresetDescription,
} from "../../core/primitives/store-profile";
import { LogoFieldController } from "../../sections/logo-field/logo-field-controller";
import { OnboardingShopActions, OnboardingShopState } from "./onboarding-shop-model";

export type OnboardingShopDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

export interface OnboardingShopParams {
  claimReview: boolean;
}

export interface OnboardingShopRouting {
  toNext(storeId: string, claimWalkthrough: boolean): void;
}

/**
 * Step 1: the shop itself. Creates one when the account has none, edits the
 * selected one otherwise; the step is revisitable, so the draft follows the
 * shop as it loads or changes.
 */
export class OnboardingShopController extends SubtreeController implements OnboardingShopActions {
  readonly state = new OnboardingShopState();
  readonly logo: LogoFieldController;
  #seededFrom: Store | null | undefined;

  constructor(
    readonly deps: OnboardingShopDeps,
    readonly params: OnboardingShopParams,
    readonly routing: OnboardingShopRouting,
  ) {
    super();
    this.subtree.put(OnboardingShopState, this.state);
    this.subtree.put(OnboardingShopActions, this);
    this.logo = this.own(new LogoFieldController(this.subtree, deps));
    this.state.claimReview.value = params.claimReview;
    this.sync(() => {
      const store = deps.storeRepository.selectedStore;
      this.state.storeId.value = store?.id ?? null;
      this.state.hostsPage.value = hostsShopPage(store);
      if (store !== this.#seededFrom) this.#seed(store);
    }, [deps.storeRepository]);
  }

  setField = (field: ProfileField, value: string) => {
    this.state.draft.update((d) => ({ ...d, [field]: value }));
    if (this.state.errors.value[field]) this.state.errors.update(({ [field]: _, ...rest }) => rest);
  };

  submit = async () => {
    if (this.state.form.value.busy) return;
    const store = this.deps.storeRepository.selectedStore;
    const draft = { ...this.state.draft.value, logoUrl: this.logo.state.url.value };
    // Outside a claim review only the name is on screen; the description keeps its preset.
    const errors = validateProfileDraft(draft, { requireDescription: this.params.claimReview && hostsShopPage(store) });
    this.state.errors.value = errors;
    if (hasErrors(errors)) return;

    this.state.form.value = busyForm;
    const r = store
      ? await updateStore({ ...this.deps, storeId: store.id, body: draftToRequest(draft, true) })
      : await createStore({ ...this.deps, body: draftToRequest(draft, false) });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.form.value = failedForm(r.errors);
      return;
    }
    this.state.form.value = idleForm;
    this.routing.toNext(r.value.id, this.params.claimReview);
  };

  #seed(store: Store | null): void {
    this.#seededFrom = store;
    const draft = storeToDraft(store);
    this.state.draft.value = this.params.claimReview ? draft : withPresetDescription(draft);
    this.state.errors.value = {};
    this.logo.seed(store?.logoUrl);
  }
}
